import { LEVELS, totalEnemies } from '../game/levels.js';
import type { GameMode, GameSelection, GameSnapshot, ResultSummary, Vec2 } from '../game/types.js';
import { MODE_TEAM_SIZE, type MatchResult, type PvpMode, type RoomListEntry, type RoomSnapshot, type TeamId } from '../net/protocol.js';

interface UIActions {
  onStart: (selection: GameSelection) => void;
  onPause: () => void;
  onResume: () => void;
  onRestart: () => void;
  onQuit: () => void;
  onNext: () => void;
  onSoundToggle: () => boolean;
  onTouchMove: (x: number, y: number) => void;
  onTouchAim: (x: number, y: number) => void;
  onTouchFire: (active: boolean) => void;
}

/** 联机 UI 回调契约：由游戏核心（PvP 侧）注入，UI 只负责转发用户操作。 */
export interface OnlineUIActions {
  onEnterLobby(): void;
  onCreateRoom(mode: PvpMode, nick: string): void;
  onJoinRoom(code: string, nick: string): void;
  onLeaveLobby(): void;
  /** 刷新大厅房间列表（GET /rooms）。 */
  onRefreshRooms(): void;
  onSetReady(ready: boolean): void;
  onStartMatch(): void;
  onLeaveRoom(): void;
  onRematch(): void;
  onExitToMenu(): void;
}

/** 对局 HUD 数据（倒计时 / 比分 / 骤死标记）。 */
export interface MatchHudData {
  remainingMs: number;
  score: [number, number];
  suddenDeath: boolean;
  /** 最近一次心跳 RTT（ms）；未知为 null/缺省。 */
  rttMs?: number | null;
  /** 击杀目标（由 MATCH_RULES 传入）；缺省则不显示目标提示。 */
  killTarget?: number;
}

export interface RadarData {
  arenaWidth: number;
  arenaDepth: number;
  /** 核心位置；PvP 对局无核心时为 null（不画核心标记）。 */
  core: Vec2 | null;
  players: Vec2[];
  enemies: Vec2[];
  pickups: Vec2[];
}

function required<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing UI element #${id}`);
  return element as T;
}

export class UIController {
  private actions: UIActions | null = null;
  private onlineActions: OnlineUIActions | null = null;
  private selection: GameSelection = { mode: 'single', levelIndex: 0, difficulty: 'normal' };
  private soundEnabled = true;
  private lastScore = -1;
  private lastCombo = -1;
  private firePointers = new Set<number>();
  /** Floating-stick reset hooks（bindTouchControls 安装；归位摇杆并清零输入向量）。 */
  private resetMoveStick: () => void = () => {};
  private resetAimStick: () => void = () => {};
  private toastSerial = 0;
  /** 大厅当前选中的对战模式。 */
  private lobbyMode: PvpMode = '1v1';
  /** 最近一次房间快照与自身 id，用于 ready 按钮文案与开始条件判定。 */
  private lastRoom: RoomSnapshot | null = null;
  private lastSelfId: string | null = null;
  private currentRoomCode = '';

  private readonly menu = required<HTMLElement>('menu-screen');
  private readonly hud = required<HTMLElement>('hud');
  private readonly pause = required<HTMLElement>('pause-screen');
  private readonly result = required<HTMLElement>('result-screen');
  private readonly howTo = required<HTMLElement>('how-to-screen');
  private readonly lobby = required<HTMLElement>('lobby-screen');
  private readonly room = required<HTMLElement>('room-screen');
  private readonly matchResult = required<HTMLElement>('match-result-screen');
  private readonly touchControls = required<HTMLElement>('touch-controls');
  private readonly moveCapture = required<HTMLElement>('move-capture');
  private readonly aimCapture = required<HTMLElement>('aim-capture');
  private readonly joystick = required<HTMLElement>('joystick-zone');
  private readonly joystickKnob = required<HTMLElement>('joystick-knob');
  private readonly aimStick = required<HTMLElement>('aim-stick-zone');
  private readonly aimStickKnob = required<HTMLElement>('aim-stick-knob');
  private readonly touchFire = required<HTMLButtonElement>('touch-fire');
  private readonly radar = required<HTMLCanvasElement>('radar');
  private readonly radarContext = this.radar.getContext('2d')!;
  private readonly banner = required<HTMLElement>('banner');
  private readonly impactFlash = required<HTMLElement>('impact-flash');
  private readonly debugPanel = required<HTMLElement>('debug-panel');
  /** 上一帧对局比分（setMatchHud 用于检测比分变化触发 pop 动画）；null 表示 HUD 隐藏中。 */
  private lastMatchScore: [number, number] | null = null;
  /** 暂停屏提示文案的本地默认值（showPause(true) 覆盖后恢复用）。 */
  private pauseTipDefault: string | null = null;

  constructor() {
    this.bindChoices();
    this.bindButtons();
    this.bindTouchControls();
    this.bindOnlineControls();
    this.updateMenuPreview();
  }

  setActions(actions: UIActions): void {
    this.actions = actions;
  }

  setOnlineActions(actions: OnlineUIActions): void {
    this.onlineActions = actions;
  }

  getSelection(): GameSelection {
    return { ...this.selection };
  }

  setSelection(selection: Partial<GameSelection>): void {
    this.selection = { ...this.selection, ...selection };
    this.syncChoiceStates();
    this.updateMenuPreview();
  }

  showMenu(): void {
    this.menu.hidden = false;
    this.menu.classList.add('is-visible');
    this.hud.hidden = true;
    this.pause.hidden = true;
    this.result.hidden = true;
    this.howTo.hidden = true;
    this.hideOnlineScreens();
    this.setMatchHud(null);
    this.touchControls.hidden = true;
    document.body.classList.remove('in-game');
    document.body.classList.remove('touch-mode');
    this.resetTouchControls();
  }

  showGame(mode: GameMode): void {
    this.menu.classList.remove('is-visible');
    this.menu.hidden = true;
    this.hud.hidden = false;
    this.pause.hidden = true;
    this.result.hidden = true;
    this.howTo.hidden = true;
    this.hideOnlineScreens();
    // 非联机模式确保对局 HUD 处于隐藏状态
    if ((mode as string) !== 'pvp') this.setMatchHud(null);
    required<HTMLElement>('player-two-plate').hidden = mode !== 'coop';
    document.body.classList.add('in-game');
    this.updateTouchVisibility();
    window.setTimeout(() => required<HTMLElement>('controls-hint').classList.add('fade'), 5200);
  }

  showPause(online = false): void {
    this.pause.hidden = false;
    this.pause.classList.add('is-visible');
    // 联机对局：隐藏「重新开始」（PvP 走房间 rematch 流程），并换成「退出将判负」提示
    required<HTMLButtonElement>('restart-button').hidden = online;
    const tip = this.pause.querySelector<HTMLElement>('.modal-panel > p:not(.eyebrow)');
    if (tip) {
      this.pauseTipDefault ??= tip.textContent ?? '';
      tip.textContent = online
        ? '联机对局仍在进行中（服务端计时）。中途离开将直接判负，确认要离开吗？'
        : this.pauseTipDefault;
    }
    this.resetTouchControls();
  }

  hidePause(): void {
    this.pause.classList.remove('is-visible');
    this.pause.hidden = true;
    this.updateTouchVisibility();
  }

  showResult(summary: ResultSummary): void {
    this.pause.hidden = true;
    this.result.hidden = false;
    this.result.classList.add('is-visible');
    this.touchControls.hidden = true;
    const lastLevel = summary.levelIndex >= LEVELS.length - 1;
    required<HTMLElement>('result-emblem').textContent = summary.victory ? '★' : '×';
    required<HTMLElement>('result-kicker').textContent = summary.victory ? 'LEVEL CLEAR' : 'MISSION FAILED';
    required<HTMLElement>('result-title').textContent = summary.victory ? (lastLevel ? '月堡解放！' : '核心安全！') : '核心失守！';
    required<HTMLElement>('result-message').textContent = summary.victory
      ? lastLevel
        ? '皇冠巨坦变成了一堆冒烟的积木。整个玩具世界都在为你欢呼！'
        : '玩具坦克们已经落荒而逃。下一片战场正等着你。'
      : '别灰心，重新布置路线，再用反弹炮弹给它们一点颜色看看。';
    required<HTMLElement>('result-score').textContent = this.formatScore(summary.score);
    required<HTMLElement>('result-combo').textContent = `x${summary.maxCombo}`;
    required<HTMLElement>('result-core').textContent = `${summary.coreHealth} / ${summary.coreMaxHealth}`;
    const stars = [...required<HTMLElement>('result-stars').children] as HTMLElement[];
    stars.forEach((star, index) => {
      const earned = index < summary.stars;
      star.classList.toggle('earned', earned);
      star.classList.toggle('empty', !earned);
    });
    const primary = required<HTMLButtonElement>('result-primary-button');
    primary.hidden = !summary.victory;
    const primaryLabel = primary.querySelector('b');
    if (primaryLabel) primaryLabel.textContent = lastLevel ? '再战一轮' : '下一关';
  }

  /** 进入联机大厅：回填昵称并切屏。 */
  showLobby(nick: string): void {
    const nickInput = required<HTMLInputElement>('lobby-nick');
    if (nick) nickInput.value = nick;
    required<HTMLElement>('lobby-hint').hidden = true;
    this.showOnlineScreen(this.lobby);
  }

  /**
   * 渲染大厅房间列表。
   * @param rooms null 表示加载中；否则为已取回的房间条目（空数组 = 暂无可加入房间）。
   * @param error 拉取失败时的提示文案（优先于 rooms 展示）。
   */
  renderRoomList(rooms: RoomListEntry[] | null, error?: string): void {
    const list = required<HTMLElement>('room-list');
    const hint = required<HTMLElement>('room-list-hint');
    list.innerHTML = '';
    if (error !== undefined) {
      hint.textContent = error;
      hint.hidden = false;
      return;
    }
    if (rooms === null) {
      hint.textContent = '正在加载房间列表…';
      hint.hidden = false;
      return;
    }
    if (rooms.length === 0) {
      hint.textContent = '暂无可加入的房间，创建一个或等好友建房吧';
      hint.hidden = false;
      return;
    }
    hint.hidden = true;
    for (const entry of rooms) {
      const row = document.createElement('li');
      row.className = 'room-list-row';
      const code = document.createElement('b');
      code.className = 'room-list-code';
      code.textContent = entry.code;
      const mode = document.createElement('span');
      mode.className = 'room-list-mode';
      mode.textContent = entry.mode === '1v1' ? '1v1' : '2v2';
      const players = document.createElement('span');
      players.className = 'room-list-players';
      players.textContent = `${entry.players}/${entry.capacity}`;
      const join = document.createElement('button');
      join.className = 'room-list-join';
      join.type = 'button';
      join.dataset.roomCode = entry.code;
      join.textContent = '加入';
      row.append(code, mode, players, join);
      list.appendChild(row);
    }
  }

  /** 房间全量刷新：房间码、两队成员、ready/房主标记、准备与开始按钮态。 */
  showRoom(room: RoomSnapshot, selfId: string): void {
    this.lastRoom = room;
    this.lastSelfId = selfId;
    this.currentRoomCode = room.code;
    required<HTMLElement>('room-code').textContent = room.code;
    required<HTMLElement>('room-mode-label').textContent = room.mode === '1v1' ? '1v1 单挑' : '2v2 团战';
    this.renderTeamList(required<HTMLElement>('team-alpha-list'), room, 'alpha', selfId);
    this.renderTeamList(required<HTMLElement>('team-bravo-list'), room, 'bravo', selfId);

    const self = room.members.find((member) => member.playerId === selfId);
    // 准备按钮文案随自己的 ready 态切换
    required<HTMLButtonElement>('room-ready-button').textContent = self?.ready ? '✓ 取消准备' : '✓ 准备';

    // 开始按钮仅房主可见；满员且非房主全员 ready 才可点击，否则置灰并提示
    const startButton = required<HTMLButtonElement>('room-start-button');
    const hint = required<HTMLElement>('room-hint');
    const capacity = MODE_TEAM_SIZE[room.mode] * 2;
    const isHost = self?.host ?? false;
    startButton.hidden = !isHost;
    if (isHost) {
      const full = room.members.length >= capacity;
      const othersReady = room.members.filter((member) => !member.host).every((member) => member.ready && member.connected);
      const canStart = full && othersReady;
      startButton.disabled = !canStart;
      hint.textContent = !full
        ? `等待玩家加入（${room.members.length} / ${capacity}），把房间码发给好友吧`
        : othersReady
          ? '全员就绪，可以开战！'
          : '等待所有玩家准备…';
    } else {
      hint.textContent = self?.ready ? '已准备，等待房主开始对战' : '点击「准备」，等待房主开始对战';
    }
    if (this.room.hidden) this.showOnlineScreen(this.room);
  }

  /** 联机结算屏：胜负徽章、比分、成员统计表（K-D / 伤害 / MVP）。 */
  showMatchResult(result: MatchResult, selfId: string, selfTeam: TeamId): void {
    const draw = result.winnerTeam === null;
    const win = !draw && result.winnerTeam === selfTeam;
    const emblem = required<HTMLElement>('match-result-emblem');
    emblem.classList.toggle('lose', !win && !draw);
    emblem.classList.toggle('draw', draw);
    emblem.textContent = win ? '★' : draw ? '＝' : '×';
    required<HTMLElement>('match-result-kicker').textContent = win ? 'VICTORY' : draw ? 'DRAW' : 'DEFEAT';
    required<HTMLElement>('match-result-title').textContent = win ? '胜利！' : draw ? '势均力敌' : '惜败…';
    // protocol.ts 契约新增的可选字段；worker 侧未部署前缺省为空
    const quitters = (result as MatchResult & { quitters?: { playerId: string; name: string }[] }).quitters ?? [];
    const quitterIds = new Set(quitters.map((q) => q.playerId));
    const teamOf = (id: string): TeamId | undefined => result.stats.find((s) => s.playerId === id)?.team;
    const selfQuit = quitters.some((q) => teamOf(q.playerId) === selfTeam);
    const enemyQuit = quitters.some((q) => {
      const team = teamOf(q.playerId);
      return team !== undefined && team !== selfTeam;
    });
    // forfeit 细分：对方有人退出且我方胜 → 不战而胜；己方有人退出且我方负 → 队友离开
    const forfeitText = win && enemyQuit ? '对手离开战场，不战而胜' : !win && !draw && selfQuit ? '队友离开，战斗提前结束' : '对方弃权';
    const reasonText: Record<MatchResult['reason'], string> = {
      kill_target: '率先达成击杀目标',
      timeout: '常规时间结束，击杀数定胜负',
      sudden_death: '骤死时刻完成绝杀',
      forfeit: forfeitText,
    };
    const minutes = Math.floor(result.durationSec / 60);
    const seconds = Math.round(result.durationSec % 60);
    required<HTMLElement>('match-result-reason').textContent =
      `${reasonText[result.reason]} · 用时 ${minutes}:${String(seconds).padStart(2, '0')}`;
    required<HTMLElement>('match-result-score').textContent = `${result.score[0]} : ${result.score[1]}`;

    // 统计表：alpha 队在前，队内按击杀数排序；自己行高亮，退出者打徽标
    const stats = required<HTMLElement>('match-stats');
    stats.innerHTML = '';
    const header = document.createElement('div');
    header.className = 'ms-row ms-head';
    header.innerHTML = '<span class="ms-name">玩家</span><span class="ms-kd">K / D</span><span class="ms-dmg">伤害</span><span class="ms-badge"></span>';
    stats.appendChild(header);
    [...result.stats]
      .sort((a, b) => (a.team === b.team ? b.kills - a.kills : a.team === 'alpha' ? -1 : 1))
      .forEach((player) => {
        const quit = quitterIds.has(player.playerId);
        const row = document.createElement('div');
        row.className = 'ms-row';
        row.dataset.team = player.team;
        if (player.mvp) row.dataset.mvp = 'true';
        if (quit) row.dataset.quit = 'true';
        if (player.playerId === selfId) row.dataset.self = 'true';
        const name = document.createElement('span');
        name.className = 'ms-name';
        name.textContent = player.name;
        const kd = document.createElement('span');
        kd.className = 'ms-kd';
        kd.textContent = `${player.kills} / ${player.deaths}`;
        const damage = document.createElement('span');
        damage.className = 'ms-dmg';
        damage.textContent = String(Math.round(player.damageDealt));
        const badge = document.createElement('span');
        badge.className = 'ms-badge';
        badge.textContent = [player.mvp ? 'MVP' : '', quit ? '中途退出' : ''].filter(Boolean).join(' · ');
        row.append(name, kd, damage, badge);
        stats.appendChild(row);
      });
    this.showOnlineScreen(this.matchResult);
  }

  /** 隐藏联机三屏（大厅 / 房间 / 结算），恢复 screen 互斥。 */
  hideOnlineScreens(): void {
    [this.lobby, this.room, this.matchResult].forEach((screen) => {
      screen.classList.remove('is-visible');
      screen.hidden = true;
    });
  }

  /** 对局 HUD：null 隐藏；否则更新倒计时（m:ss）、比分与骤死提示。 */
  setMatchHud(hud: MatchHudData | null): void {
    const container = required<HTMLElement>('match-hud');
    if (!hud) {
      container.hidden = true;
      this.lastMatchScore = null;
      return;
    }
    container.hidden = false;
    // 比分变化时触发 pop 弹跳动画（styles.css 的 .match-score-line.pop 纯 CSS 钩子）
    if (this.lastMatchScore && (this.lastMatchScore[0] !== hud.score[0] || this.lastMatchScore[1] !== hud.score[1])) {
      const scoreLine = container.querySelector<HTMLElement>('.match-score-line');
      if (scoreLine) {
        scoreLine.classList.remove('pop');
        void scoreLine.offsetWidth; // 强制回流以重新触发动画
        scoreLine.classList.add('pop');
      }
    }
    this.lastMatchScore = [hud.score[0], hud.score[1]];
    const totalSec = Math.max(0, Math.ceil(hud.remainingMs / 1000));
    required<HTMLElement>('match-timer').textContent =
      `${Math.floor(totalSec / 60)}:${String(totalSec % 60).padStart(2, '0')}`;
    required<HTMLElement>('match-score-alpha').textContent = String(hud.score[0]);
    required<HTMLElement>('match-score-bravo').textContent = String(hud.score[1]);
    required<HTMLElement>('sudden-death-badge').hidden = !hud.suddenDeath;
    container.classList.toggle('sudden', hud.suddenDeath);
    // 最后 30 秒进入紧急态（骤死已有自己的 .sudden 表现，不叠加）
    container.classList.toggle('urgent', !hud.suddenDeath && hud.remainingMs <= 30000);
    // 击杀目标提示：动态维护，无目标时隐藏
    let target = container.querySelector<HTMLElement>('.match-target');
    if (hud.killTarget !== undefined) {
      if (!target) {
        target = document.createElement('span');
        target.className = 'match-target';
        container.appendChild(target);
      }
      target.hidden = false;
      target.textContent = `目标 ${hud.killTarget} 杀`;
    } else if (target) {
      target.hidden = true;
    }
    const rttEl = required<HTMLElement>('match-rtt');
    if (hud.rttMs !== null && hud.rttMs !== undefined) {
      rttEl.hidden = false;
      rttEl.textContent = `${Math.round(hud.rttMs)}ms`;
      // 延迟分档着色：<80 流畅 / <160 可用 / 更高偏卡
      rttEl.dataset.level = hud.rttMs < 80 ? 'good' : hud.rttMs < 160 ? 'ok' : 'bad';
    } else {
      rttEl.hidden = true;
    }
  }

  update(snapshot: GameSnapshot): void {
    // types.ts 由联机侧并行扩展出 'pvp'，这里用宽化写法避免编译期类型依赖
    const isPvp = (snapshot as { mode?: string }).mode === 'pvp';
    const level = LEVELS[snapshot.levelIndex];
    const waveDots = required<HTMLElement>('wave-dots');
    const coreCluster = required<HTMLElement>('core-cluster');
    const bossBar = required<HTMLElement>('boss-bar');
    if (isPvp) {
      // 联机对局：隐藏波次进度 / 核心血量 / Boss 血条等 PvE 元素
      waveDots.hidden = true;
      coreCluster.hidden = true;
      bossBar.hidden = true;
      required<HTMLElement>('hud-level-name').textContent = '联机对战 ONLINE';
      required<HTMLElement>('hud-objective').textContent = '击毁敌方坦克';
    } else if (level) {
      waveDots.hidden = false;
      coreCluster.hidden = false;
      required<HTMLElement>('hud-level-name').textContent = `关卡 ${level.id} · ${level.name}`;
      required<HTMLElement>('hud-objective').textContent = level.objective;
      required<HTMLElement>('core-health-text').textContent = `${snapshot.coreHealth} / ${snapshot.coreMaxHealth}`;
      required<HTMLElement>('core-health-fill').style.width = `${Math.max(0, snapshot.coreHealth / snapshot.coreMaxHealth) * 100}%`;
    }
    required<HTMLElement>('score-value').textContent = this.formatScore(snapshot.score);

    snapshot.players.forEach((player, index) => {
      const prefix = index === 0 ? 'p1' : 'p2';
      required<HTMLElement>(`${prefix}-health-fill`).style.width = `${Math.max(0, player.health / player.maxHealth) * 100}%`;
      required<HTMLElement>(`${prefix}-lives`).textContent = player.lives > 0 ? '♥'.repeat(player.lives) : '—';
      required<HTMLElement>(`${prefix}-status`).textContent = player.status;
      const powerRow = required<HTMLElement>(`${prefix}-power-row`);
      const label = powerRow.querySelector('span');
      if (label) label.textContent = player.powerLabel;
      required<HTMLElement>(`player-${index === 0 ? 'one' : 'two'}-plate`).classList.toggle('is-down', !player.alive);
    });

    const combo = required<HTMLElement>('combo-badge');
    const comboValue = combo.querySelector('strong');
    if (comboValue) comboValue.textContent = `x${snapshot.combo}`;
    if (snapshot.combo !== this.lastCombo && snapshot.combo > 1) {
      combo.classList.remove('pop');
      void combo.offsetWidth;
      combo.classList.add('pop');
      window.setTimeout(() => combo.classList.remove('pop'), 180);
    }

    if (snapshot.score !== this.lastScore) {
      required<HTMLElement>('score-value').classList.add('score-pop');
      window.setTimeout(() => required<HTMLElement>('score-value').classList.remove('score-pop'), 170);
    }
    this.lastScore = snapshot.score;
    this.lastCombo = snapshot.combo;

    if (!isPvp) {
      this.updateWaveDots(snapshot.waveIndex, snapshot.totalWaves, snapshot.enemies > 0);
      if (snapshot.bossHealth !== null && snapshot.bossMaxHealth !== null) {
        bossBar.hidden = false;
        required<HTMLElement>('boss-health-fill').style.width = `${Math.max(0, snapshot.bossHealth / snapshot.bossMaxHealth) * 100}%`;
      } else {
        bossBar.hidden = true;
      }
    }
  }

  renderRadar(data: RadarData): void {
    const ctx = this.radarContext;
    const width = this.radar.width;
    const height = this.radar.height;
    ctx.clearRect(0, 0, width, height);
    const gradient = ctx.createRadialGradient(width / 2, height / 2, 8, width / 2, height / 2, width / 2);
    gradient.addColorStop(0, 'rgba(27,68,86,.82)');
    gradient.addColorStop(1, 'rgba(5,18,32,.9)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(width / 2, height / 2, width / 2 - 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(112,237,224,.28)';
    ctx.lineWidth = 1;
    for (const radius of [25, 48, 70]) {
      ctx.beginPath();
      ctx.arc(width / 2, height / 2, radius, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.moveTo(width / 2, 8);
    ctx.lineTo(width / 2, height - 8);
    ctx.moveTo(8, height / 2);
    ctx.lineTo(width - 8, height / 2);
    ctx.stroke();

    const map = (point: Vec2): [number, number] => [
      width / 2 + point.x / data.arenaWidth * (width - 20),
      height / 2 + point.z / data.arenaDepth * (height - 20),
    ];
    const drawDot = (point: Vec2, color: string, radius: number, shape: 'circle' | 'diamond' = 'circle') => {
      const [x, y] = map(point);
      ctx.fillStyle = color;
      ctx.shadowColor = color;
      ctx.shadowBlur = 7;
      ctx.beginPath();
      if (shape === 'diamond') {
        ctx.moveTo(x, y - radius);
        ctx.lineTo(x + radius, y);
        ctx.lineTo(x, y + radius);
        ctx.lineTo(x - radius, y);
        ctx.closePath();
      } else {
        ctx.arc(x, y, radius, 0, Math.PI * 2);
      }
      ctx.fill();
      ctx.shadowBlur = 0;
    };

    if (data.core) drawDot(data.core, '#ffe35e', 5, 'diamond');
    data.pickups.forEach((point) => drawDot(point, '#69f0ce', 2.5));
    data.enemies.forEach((point) => drawDot(point, '#ff6385', 3));
    data.players.forEach((point, index) => drawDot(point, index === 0 ? '#ffd84d' : '#58e5ff', 4, 'diamond'));
  }

  showBanner(kicker: string, title: string): void {
    const small = this.banner.querySelector('small');
    const strong = this.banner.querySelector('strong');
    if (small) small.textContent = kicker;
    if (strong) strong.textContent = title;
    this.banner.classList.remove('show');
    void this.banner.offsetWidth;
    this.banner.classList.add('show');
  }

  toast(icon: string, title: string, detail: string): void {
    const stack = required<HTMLElement>('toast-stack');
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.dataset.serial = String(++this.toastSerial);
    toast.innerHTML = `<i>${icon}</i><div><b></b><small></small></div>`;
    const titleElement = toast.querySelector('b');
    const detailElement = toast.querySelector('small');
    if (titleElement) titleElement.textContent = title;
    if (detailElement) detailElement.textContent = detail;
    stack.appendChild(toast);
    window.setTimeout(() => toast.remove(), 2900);
  }

  flashImpact(strong = false): void {
    this.impactFlash.classList.remove('show', 'strong');
    if (strong) this.impactFlash.classList.add('strong');
    void this.impactFlash.offsetWidth;
    this.impactFlash.classList.add('show');
    window.setTimeout(() => this.impactFlash.classList.remove('show', 'strong'), strong ? 220 : 120);
  }

  setDebug(text: string, visible: boolean): void {
    const hidden = !visible;
    if (this.debugPanel.hidden !== hidden) this.debugPanel.hidden = hidden;
    if (visible && this.debugPanel.textContent !== text) this.debugPanel.textContent = text;
  }

  setSoundEnabled(enabled: boolean): void {
    this.soundEnabled = enabled;
    const menuButton = required<HTMLButtonElement>('sound-menu-button');
    const pauseButton = required<HTMLButtonElement>('sound-pause-button');
    menuButton.setAttribute('aria-pressed', String(!enabled));
    pauseButton.setAttribute('aria-pressed', String(!enabled));
    menuButton.textContent = enabled ? '♫ 音效' : '♩ 静音';
    pauseButton.textContent = enabled ? '♫ 音效开启' : '♩ 音效关闭';
  }

  private bindChoices(): void {
    required<HTMLElement>('mode-selector').addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-mode]');
      if (!button) return;
      this.selection.mode = button.dataset.mode as GameMode;
      this.syncChoiceStates();
      this.updateMenuPreview();
    });
    required<HTMLElement>('level-selector').addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-level]');
      if (!button) return;
      this.selection.levelIndex = Number(button.dataset.level);
      this.syncChoiceStates();
      this.updateMenuPreview();
    });
    required<HTMLElement>('difficulty-selector').addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-difficulty]');
      if (!button) return;
      this.selection.difficulty = button.dataset.difficulty as GameSelection['difficulty'];
      this.syncChoiceStates();
    });
  }

  private bindButtons(): void {
    required<HTMLButtonElement>('start-button').addEventListener('click', () => {
      // 联机模式：进入大厅而非直接开局；联机侧未注入回调时提示
      if ((this.selection as { mode?: string }).mode === 'pvp') {
        if (this.onlineActions) {
          this.onlineActions.onEnterLobby();
        } else {
          this.toast('📡', '联机模块未就绪', '请稍后重试');
        }
        return;
      }
      this.actions?.onStart(this.getSelection());
    });
    required<HTMLButtonElement>('pause-button').addEventListener('click', () => this.actions?.onPause());
    required<HTMLButtonElement>('resume-button').addEventListener('click', () => this.actions?.onResume());
    required<HTMLButtonElement>('restart-button').addEventListener('click', () => this.actions?.onRestart());
    required<HTMLButtonElement>('quit-button').addEventListener('click', () => this.actions?.onQuit());
    required<HTMLButtonElement>('result-retry-button').addEventListener('click', () => this.actions?.onRestart());
    required<HTMLButtonElement>('result-menu-button').addEventListener('click', () => this.actions?.onQuit());
    required<HTMLButtonElement>('result-primary-button').addEventListener('click', () => this.actions?.onNext());
    required<HTMLButtonElement>('how-to-button').addEventListener('click', () => {
      this.howTo.hidden = false;
      this.howTo.classList.add('is-visible');
    });
    required<HTMLButtonElement>('how-to-close').addEventListener('click', () => {
      this.howTo.classList.remove('is-visible');
      this.howTo.hidden = true;
    });
    for (const id of ['sound-menu-button', 'sound-pause-button']) {
      required<HTMLButtonElement>(id).addEventListener('click', () => {
        const enabled = this.actions?.onSoundToggle() ?? !this.soundEnabled;
        this.setSoundEnabled(enabled);
      });
    }
  }

  private bindTouchControls(): void {
    this.resetMoveStick = this.makeFloatingStick(this.moveCapture, this.joystick, this.joystickKnob, (x, y) =>
      this.actions?.onTouchMove(x, y),
    );
    this.resetAimStick = this.makeFloatingStick(this.aimCapture, this.aimStick, this.aimStickKnob, (x, y) =>
      this.actions?.onTouchAim(x, y),
    );

    this.touchFire.addEventListener('pointerdown', (event) => {
      this.firePointers.add(event.pointerId);
      this.touchFire.setPointerCapture(event.pointerId);
      this.touchFire.classList.add('pressed');
      this.actions?.onTouchFire(true);
      event.preventDefault();
    });
    const endFire = (event: PointerEvent) => {
      this.firePointers.delete(event.pointerId);
      if (this.firePointers.size === 0) {
        this.touchFire.classList.remove('pressed');
        this.actions?.onTouchFire(false);
      }
    };
    this.touchFire.addEventListener('pointerup', endFire);
    this.touchFire.addEventListener('pointercancel', endFire);
    this.touchFire.addEventListener('lostpointercapture', endFire);
    window.addEventListener('resize', () => this.updateTouchVisibility());
  }

  /**
   * Floating virtual stick（Brawl Stars / PUBG 式）：手指在捕获半区任意处按下，摇杆圆
   * 立即移到指下（钳制在捕获区内），拖拽输出 [-1,1] 向量，抬指归位并清零。
   * 返回 reset 钩子供 resetTouchControls 复用。
   */
  private makeFloatingStick(
    capture: HTMLElement,
    zone: HTMLElement,
    knob: HTMLElement,
    onInput: (x: number, y: number) => void,
  ): () => void {
    let pointer: number | null = null;
    const placeAt = (event: PointerEvent) => {
      const rect = capture.getBoundingClientRect();
      const radius = zone.offsetWidth / 2;
      const x = Math.min(Math.max(event.clientX - rect.left, radius), Math.max(rect.width - radius, radius));
      const y = Math.min(Math.max(event.clientY - rect.top, radius), Math.max(rect.height - radius, radius));
      zone.style.left = `${x - radius}px`;
      zone.style.top = `${y - radius}px`;
      zone.style.right = 'auto';
      zone.style.bottom = 'auto';
    };
    const update = (event: PointerEvent) => {
      if (pointer !== event.pointerId) return;
      const rect = zone.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const max = rect.width * 0.32;
      let dx = event.clientX - centerX;
      let dy = event.clientY - centerY;
      const length = Math.hypot(dx, dy);
      if (length > max) {
        dx = dx / length * max;
        dy = dy / length * max;
      }
      knob.style.transform = `translate(${dx}px, ${dy}px)`;
      onInput(dx / max, dy / max);
    };
    const reset = () => {
      pointer = null;
      zone.classList.remove('is-active');
      zone.style.left = '';
      zone.style.top = '';
      zone.style.right = '';
      zone.style.bottom = '';
      knob.style.transform = 'translate(0, 0)';
      onInput(0, 0);
    };
    capture.addEventListener('pointerdown', (event) => {
      pointer = event.pointerId;
      try {
        capture.setPointerCapture(event.pointerId);
      } catch {
        // 部分浏览器对已释放/合成指针抛 NotFoundError，忽略（漏捕获时 pointerup 仍会冒泡到 capture）
      }
      zone.classList.add('is-active');
      placeAt(event);
      update(event);
      event.preventDefault();
    });
    capture.addEventListener('pointermove', update);
    const end = (event: PointerEvent) => {
      if (pointer === event.pointerId) reset();
    };
    capture.addEventListener('pointerup', end);
    capture.addEventListener('pointercancel', end);
    capture.addEventListener('lostpointercapture', end);
    return reset;
  }

  /** 绑定联机三屏（大厅 / 房间 / 结算）内的所有控件。 */
  private bindOnlineControls(): void {
    // 昵称持久化：启动时回填，输入变化即保存
    const nickInput = required<HTMLInputElement>('lobby-nick');
    try {
      nickInput.value = window.localStorage.getItem('PVP_NICK') ?? '';
    } catch {
      // 隐私模式等 localStorage 不可用时静默降级
    }
    nickInput.addEventListener('input', () => {
      try {
        window.localStorage.setItem('PVP_NICK', nickInput.value.trim());
      } catch {
        // 同上，忽略持久化失败
      }
    });

    const modeRow = required<HTMLElement>('pvp-mode-selector');
    modeRow.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-pvp-mode]');
      if (!button) return;
      this.lobbyMode = button.dataset.pvpMode as PvpMode;
      modeRow.querySelectorAll<HTMLButtonElement>('[data-pvp-mode]').forEach((item) => {
        const selected = item === button;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-checked', String(selected));
      });
    });

    required<HTMLButtonElement>('lobby-create-button').addEventListener('click', () => {
      this.onlineActions?.onCreateRoom(this.lobbyMode, this.nickName());
    });

    // 房间列表：刷新按钮 + 行内「加入」（事件委托，行随列表刷新重建）
    required<HTMLButtonElement>('lobby-refresh-rooms').addEventListener('click', () => {
      this.onlineActions?.onRefreshRooms();
    });
    required<HTMLElement>('room-list').addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-room-code]');
      if (!button?.dataset.roomCode) return;
      this.onlineActions?.onJoinRoom(button.dataset.roomCode, this.nickName());
    });

    // 房间码：自动大写、仅字母数字、限 4 位
    const codeInput = required<HTMLInputElement>('lobby-code');
    codeInput.addEventListener('input', () => {
      codeInput.value = codeInput.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
    });
    const join = () => {
      const hint = required<HTMLElement>('lobby-hint');
      if (codeInput.value.length !== 4) {
        hint.textContent = '请输入 4 位房间码';
        hint.hidden = false;
        return;
      }
      hint.hidden = true;
      this.onlineActions?.onJoinRoom(codeInput.value, this.nickName());
    };
    required<HTMLButtonElement>('lobby-join-button').addEventListener('click', join);
    codeInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') join();
    });

    required<HTMLButtonElement>('lobby-back-button').addEventListener('click', () => this.onlineActions?.onLeaveLobby());
    required<HTMLButtonElement>('room-ready-button').addEventListener('click', () => {
      const self = this.lastRoom?.members.find((member) => member.playerId === this.lastSelfId);
      this.onlineActions?.onSetReady(!(self?.ready ?? false));
    });
    required<HTMLButtonElement>('room-start-button').addEventListener('click', () => this.onlineActions?.onStartMatch());
    required<HTMLButtonElement>('room-leave-button').addEventListener('click', () => this.onlineActions?.onLeaveRoom());
    required<HTMLButtonElement>('room-code').addEventListener('click', () => this.copyRoomCode());
    required<HTMLButtonElement>('match-rematch-button').addEventListener('click', () => this.onlineActions?.onRematch());
    required<HTMLButtonElement>('match-exit-button').addEventListener('click', () => this.onlineActions?.onExitToMenu());
  }

  /** 读取昵称（未填时回退默认名）。 */
  private nickName(): string {
    return required<HTMLInputElement>('lobby-nick').value.trim() || '坦克手';
  }

  /** 点击房间码复制到剪贴板。 */
  private copyRoomCode(): void {
    const code = this.currentRoomCode;
    if (!code) return;
    const done = () => this.toast('⧉', '房间码已复制', `分享给好友：${code}`);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(() => this.toast('✦', '房间码', code));
    } else {
      this.toast('✦', '房间码', code);
    }
  }

  /** 联机屏互斥切换：隐藏全部既有屏，仅显示目标屏。 */
  private showOnlineScreen(target: HTMLElement): void {
    this.menu.classList.remove('is-visible');
    this.menu.hidden = true;
    this.hud.hidden = true;
    this.pause.hidden = true;
    this.result.hidden = true;
    this.howTo.hidden = true;
    this.touchControls.hidden = true;
    document.body.classList.remove('in-game');
    [this.lobby, this.room, this.matchResult].forEach((screen) => {
      const active = screen === target;
      screen.classList.toggle('is-visible', active);
      screen.hidden = !active;
    });
  }

  /** 渲染一队成员列表（房主皇冠、ready 勾、离线置灰、空位占位）。 */
  private renderTeamList(list: HTMLElement, room: RoomSnapshot, team: TeamId, selfId: string): void {
    list.innerHTML = '';
    const capacity = MODE_TEAM_SIZE[room.mode];
    const members = room.members.filter((member) => member.team === team).sort((a, b) => a.slot - b.slot);
    for (let i = 0; i < capacity; i += 1) {
      const member = members[i];
      const row = document.createElement('li');
      if (!member) {
        row.className = 'member-row empty';
        row.textContent = '等待玩家加入…';
      } else {
        row.className = 'member-row';
        row.classList.toggle('is-self', member.playerId === selfId);
        row.classList.toggle('offline', !member.connected);
        const crown = document.createElement('span');
        crown.className = 'host-crown';
        crown.textContent = member.host ? '👑' : '';
        const name = document.createElement('b');
        name.className = 'member-name';
        name.textContent = member.name + (member.playerId === selfId ? '（我）' : '');
        const status = document.createElement('span');
        status.className = 'ready-mark';
        if (!member.connected) {
          status.textContent = '离线';
        } else if (member.ready) {
          status.textContent = '✓ 已准备';
          status.classList.add('ready');
        } else {
          status.textContent = '未准备';
        }
        row.append(crown, name, status);
      }
      list.appendChild(row);
    }
  }

  private updateMenuPreview(): void {
    const level = LEVELS[this.selection.levelIndex];
    required<HTMLElement>('menu-level-title').textContent = level.objective;
    required<HTMLElement>('menu-level-description').textContent = level.description;
    required<HTMLElement>('menu-enemy-count').textContent = `${totalEnemies(level)} 敌军`;
    required<HTMLElement>('menu-theme-tag').textContent = level.themeTag;
    const controls = required<HTMLElement>('menu-controls');
    controls.innerHTML = this.selection.mode === 'coop'
      ? '<span><kbd>WASD</kbd> 1P</span><span><kbd>方向键</kbd> 2P</span><span><kbd>空格 / Enter</kbd> 开火</span>'
      : '<span><kbd>WASD</kbd> 移动</span><span><kbd>鼠标</kbd> 瞄准</span><span><kbd>空格</kbd> 开火</span>';
  }

  private syncChoiceStates(): void {
    document.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((button) => {
      const selected = button.dataset.mode === this.selection.mode;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-checked', String(selected));
    });
    document.querySelectorAll<HTMLButtonElement>('[data-level]').forEach((button) => {
      const selected = Number(button.dataset.level) === this.selection.levelIndex;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-checked', String(selected));
    });
    document.querySelectorAll<HTMLButtonElement>('[data-difficulty]').forEach((button) => {
      const selected = button.dataset.difficulty === this.selection.difficulty;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-checked', String(selected));
    });
  }

  private updateWaveDots(waveIndex: number, totalWaves: number, active: boolean): void {
    const container = required<HTMLElement>('wave-dots');
    if (container.children.length !== totalWaves) {
      container.innerHTML = '';
      for (let i = 0; i < totalWaves; i += 1) container.appendChild(document.createElement('i'));
    }
    [...container.children].forEach((dot, index) => {
      dot.classList.toggle('done', index < waveIndex);
      dot.classList.toggle('active', index === waveIndex && active);
    });
  }

  private updateTouchVisibility(): void {
    const coarse = window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 760;
    const inGame = document.body.classList.contains('in-game') && this.pause.hidden && this.result.hidden;
    this.touchControls.hidden = !(coarse && inGame);
    // touch-mode 驱动 HUD 重排（雷达移右上角等）；暂停/结算覆盖层期间保持，避免底下 HUD 跳位。
    document.body.classList.toggle('touch-mode', coarse && document.body.classList.contains('in-game'));
  }

  private resetTouchControls(): void {
    this.resetMoveStick();
    this.resetAimStick();
    this.firePointers.clear();
    this.touchFire.classList.remove('pressed');
    this.actions?.onTouchFire(false);
  }

  private formatScore(score: number): string {
    return Math.max(0, Math.round(score)).toString().padStart(6, '0');
  }
}
